import type { AudioClip, Clip, ClipFade, FadeCurve, MidiClip, Project } from '@/domain/types';
import {
  clipEndTicks,
  clipLengthTicks,
  effectivePlaybackRate,
  sourceSecondsBetween,
  tempoMapOf,
  tickAfterSourceSeconds,
} from '@/domain/selectors';
import { insertClips, removeClips, reparentClip, resortTrackClips, withClips } from '@/domain/mutate';
import {
  MAX_PITCH_SEMITONES,
  MAX_PLAYBACK_RATE,
  MIN_PITCH_SEMITONES,
  MIN_PLAYBACK_RATE,
} from '@/domain/project';
import { mirrorWarpMarkers, sliceWarp } from '@/domain/warpMap';
import { headroomAfter, headroomBefore, recutSource } from '@/domain/sourceSpan';
import {
  canRipple,
  insertSpans,
  liftSpans,
  movedAnywhere,
  type MovedClip,
} from '@/domain/ripple';
import { tempoAt, type NormalisedTempoMap } from '@/timing/tempoMap';
import { newClipId, newNoteId, newWarpMarkerId } from '@/util/id';
import { defineCommand } from './registry';
import { boolParam, enumParam, idParam, idsParam, intParam, numberParam, omitted, stringParam } from './params';

const FADE_CURVES: readonly FadeCurve[] = ['linear', 'equalPower', 'exponential'];

/** Source seconds consumed by a timeline span starting at `startTicks`. */
function sourceSecondsForSpan(project: Project, clip: AudioClip, startTicks: number, endTicks: number): number {
  return sourceSecondsBetween(project, clip, startTicks, endTicks);
}

export const addAudioClip = defineCommand<{
  clipId: string;
  trackId: string;
  assetId: string;
  name: string;
  startTicks: number;
  offsetSec: number;
  durationSec: number;
}>({
  type: 'clip.addAudio',
  label: (p) => `Add clip "${p.name}"`,
  parse: (raw) => {
    const clipId = raw.clipId === undefined ? newClipId() : idParam(raw.clipId);
    const trackId = idParam(raw.trackId);
    const assetId = idParam(raw.assetId);
    const startTicks = intParam(raw.startTicks, 0, Number.MAX_SAFE_INTEGER, 0);
    const offsetSec = numberParam(raw.offsetSec, 0, 86400, 0);
    const durationSec = numberParam(raw.durationSec, 0, 86400);
    if (!clipId || !trackId || !assetId || startTicks === null || offsetSec === null || durationSec === null) {
      return null;
    }
    return {
      clipId,
      trackId,
      assetId,
      name: stringParam(raw.name, 128, 'Audio') ?? 'Audio',
      startTicks,
      offsetSec,
      durationSec,
    };
  },
  apply: (project, p) => {
    const track = project.tracks[p.trackId];
    if (!track || !project.assets[p.assetId] || project.clips[p.clipId]) return project;
    const clip: AudioClip = {
      id: p.clipId,
      kind: 'audio',
      gainEnvelope: [],
      trackId: p.trackId,
      name: p.name,
      startTicks: p.startTicks,
      muted: false,
      color: null,
      takeLane: 0,
      assetId: p.assetId,
      offsetSec: p.offsetSec,
      durationSec: p.durationSec,
      gain: 1,
      fadeIn: { durationSec: 0, curve: 'equalPower', curvature: 0 },
      fadeOut: { durationSec: 0, curve: 'equalPower', curvature: 0 },
      playbackRate: 1,
      preservePitch: true,
      warpSourceBpm: null,
      warpMarkers: [],
      reversed: false,
      pitchSemitones: 0,
      formantSemitones: null,
    };
    return insertClips(project, [clip]);
  },
});

export const deleteClips = defineCommand<{ clipIds: string[]; ripple: boolean }>({
  type: 'clip.delete',
  label: (p) =>
    p.ripple
      ? p.clipIds.length === 1
        ? 'Delete clip and close the gap'
        : `Delete ${p.clipIds.length} clips and close the gap`
      : p.clipIds.length === 1
        ? 'Delete clip'
        : `Delete ${p.clipIds.length} clips`,
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    if (!clipIds || clipIds.length === 0) return null;
    return { clipIds, ripple: boolParam(raw.ripple, false) ?? false };
  },
  apply: (project, p) => {
    if (!p.ripple) return removeClips(project, p.clipIds);

    // Rippling closes the hole each clip leaves, on its own track. Done one clip
    // at a time and from the end backwards, so removing an earlier clip cannot
    // move a later one out from under the next step.
    const clips = p.clipIds
      .map((id) => project.clips[id])
      .filter((clip): clip is Clip => Boolean(clip))
      .sort((a, b) => b.startTicks - a.startTicks);

    let next = project;
    for (const clip of clips) {
      const length = Math.round(clipEndTicks(next, clip) - clip.startTicks);
      next = removeClips(next, [clip.id]);
      next = rippleTrack(next, clip.trackId, clip.startTicks, -length);
    }
    return next;
  },
});

/**
 * Moves everything at or after a point on one track, and the automation with it.
 *
 * Ripple that shifted the audio but left the volume curve where it was would
 * silently de-synchronise the track: the edit would look right and sound wrong.
 * The same reasoning as the range operations, applied to a single clip edit.
 */
function rippleTrack(
  project: Project,
  trackId: string,
  fromTicks: number,
  deltaTicks: number,
  except: readonly string[] = [],
): Project {
  if (deltaTicks === 0) return project;
  const skip = new Set(except);

  const clips = { ...project.clips };
  for (const clip of Object.values(project.clips)) {
    if (clip.trackId !== trackId || clip.startTicks < fromTicks || skip.has(clip.id)) continue;
    clips[clip.id] = { ...clip, startTicks: Math.max(0, clip.startTicks + deltaTicks) };
  }

  const track = project.tracks[trackId];
  const automation = { ...project.automation };
  for (const laneId of track?.automationLaneIds ?? []) {
    const lane = project.automation[laneId];
    if (!lane) continue;
    automation[laneId] = {
      ...lane,
      points: lane.points.map((point) =>
        point.tick >= fromTicks ? { ...point, tick: Math.max(0, point.tick + deltaTicks) } : point,
      ),
    };
  }

  return resortTrackClips({ ...project, clips, automation }, trackId);
}

export const moveClips = defineCommand<{ clipIds: string[]; deltaTicks: number; targetTrackIds: string[] | null }>({
  type: 'clip.move',
  label: (p) => (p.clipIds.length === 1 ? 'Move clip' : `Move ${p.clipIds.length} clips`),
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    const deltaTicks = intParam(raw.deltaTicks, -Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 0);
    if (!clipIds || clipIds.length === 0 || deltaTicks === null) return null;
    const targetTrackIds = raw.targetTrackIds === undefined ? null : idsParam(raw.targetTrackIds);
    if (targetTrackIds && targetTrackIds.length !== clipIds.length) return null;
    return { clipIds, deltaTicks, targetTrackIds };
  },
  coalesceKey: (p) => `move:${p.clipIds.join(',')}`,
  apply: (project, p) => {
    // Clamp as a group so a multi-clip drag keeps its internal spacing when it
    // runs into the start of the timeline.
    let clampedDelta = p.deltaTicks;
    for (const id of p.clipIds) {
      const clip = project.clips[id];
      if (clip) clampedDelta = Math.max(clampedDelta, -clip.startTicks);
    }

    let next = withClips(project, p.clipIds, (clip) => ({
      ...clip,
      startTicks: Math.max(0, clip.startTicks + clampedDelta),
    }));

    if (p.targetTrackIds) {
      p.clipIds.forEach((clipId, index) => {
        const targetTrackId = p.targetTrackIds![index];
        const clip = next.clips[clipId];
        const target = next.tracks[targetTrackId];
        if (!clip || !target) return;
        // Audio cannot land on an instrument lane, and vice versa.
        const compatible =
          (clip.kind === 'audio' && (target.kind === 'audio' || target.kind === 'bus')) ||
          (clip.kind === 'midi' && target.kind === 'instrument');
        if (compatible) next = reparentClip(next, clipId, targetTrackId);
      });
    }

    const touched = new Set(p.clipIds.map((id) => next.clips[id]?.trackId).filter(Boolean) as string[]);
    for (const trackId of touched) next = resortTrackClips(next, trackId);
    return next;
  },
});

/**
 * Closes the gap moved clips left, and opens one where they landed.
 *
 * This is a second command rather than a flag on `clip.move`, because a drag
 * dispatches a *stream* of small moves — one per pointer event — and rippling
 * each of them would shove the neighbours a clip-length further every few
 * milliseconds. So the move stays relative and live, and the ripple happens once
 * when the pointer comes up, given the positions the clips started from. It
 * shares the move's coalesce key, so the whole gesture is still one "Undo".
 *
 * It is a lift and an insert, in that order: everything after the old span
 * closes up by its length, and then everything at or after where it was dropped
 * opens by the same amount. Order matters — computing both against the original
 * timeline would let the two shifts cancel, and drop the clips on top of a
 * neighbour instead of between two of them. With several source or destination
 * tracks the lifts all happen before any of the inserts, for the same reason.
 *
 * **A selection ripples as a block, so it has to be one.** `canRipple` refuses
 * when an unselected clip starts inside the stretch the selection covers, since
 * closing that stretch would drag material the user did not select. The drag
 * then stands as an ordinary move, which is what it was before, and the caller
 * says so.
 */
export const rippleMovedClips = defineCommand<{ moved: MovedClip[] }>({
  type: 'clip.rippleMoved',
  label: (p) => (p.moved.length === 1 ? 'Move clip and close the gap' : 'Move clips and close the gap'),
  parse: (raw) => {
    const moved = readMovedClips(raw);
    return moved && moved.length > 0 ? { moved } : null;
  },
  // The key the move itself used, so the ripple joins that undo entry instead of
  // adding a second thing the user would have to undo separately.
  coalesceKey: (p) => `move:${p.moved.map((m) => m.clipId).join(',')}`,
  apply: (project, p) => {
    const moved = p.moved.filter((entry) => project.clips[entry.clipId]);
    if (!movedAnywhere(project, moved) || !canRipple(project, moved)) return project;

    const skip = moved.map((entry) => entry.clipId);
    // From the old span's *end*, which is the edge the material behind it abuts.
    // `canRipple` has already established that nothing unmoved starts inside the
    // span, so measuring from the other edge would shift the same clips; the end
    // is the one that says what the number means.
    let next = project;
    for (const span of liftSpans(project, moved)) {
      next = rippleTrack(next, span.trackId, span.endTicks, -(span.endTicks - span.startTicks), skip);
    }
    for (const span of insertSpans(next, moved)) {
      next = rippleTrack(next, span.trackId, span.startTicks, span.endTicks - span.startTicks, skip);
    }
    return next;
  },
});

/** Reads the moved-clip list a ripple needs, rejecting anything malformed. */
function readMovedClips(raw: Record<string, unknown>): MovedClip[] | null {
  if (!Array.isArray(raw.moved)) return null;
  const out: MovedClip[] = [];
  for (const entry of raw.moved) {
    if (typeof entry !== 'object' || entry === null) return null;
    const record = entry as Record<string, unknown>;
    const clipId = idParam(record.clipId);
    const fromTrackId = idParam(record.fromTrackId);
    const fromStartTicks = intParam(record.fromStartTicks, 0, Number.MAX_SAFE_INTEGER);
    if (!clipId || !fromTrackId || fromStartTicks === null) return null;
    out.push({ clipId, fromTrackId, fromStartTicks });
  }
  return out;
}

export const trimClip = defineCommand<{
  clipId: string;
  edge: 'start' | 'end';
  toTick: number;
  ripple: boolean;
}>({
  type: 'clip.trim',
  label: (p) => (p.ripple ? 'Trim clip and move what follows' : 'Trim clip'),
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const edge = enumParam(raw.edge, ['start', 'end'] as const);
    const toTick = intParam(raw.toTick, 0, Number.MAX_SAFE_INTEGER);
    if (!clipId || !edge || toTick === null) return null;
    return { clipId, edge, toTick, ripple: boolParam(raw.ripple, false) ?? false };
  },
  coalesceKey: (p) => `trim:${p.clipId}:${p.edge}`,
  apply: (project, p) => {
    const trimmed = applyTrim(project, p);
    if (!p.ripple) return trimmed;

    const before = project.clips[p.clipId];
    const after = trimmed.clips[p.clipId];
    if (!before || !after) return trimmed;

    // Ripple follows the change in *length*, and shifts from the clip's old end.
    // Measuring from the end rather than from the start is what makes trimming
    // either edge behave the same way: the material after the clip keeps its
    // distance from the clip's end, which is the join the edit was about.
    const oldEnd = Math.round(clipEndTicks(project, before));
    const delta = Math.round(clipEndTicks(trimmed, after)) - oldEnd;
    if (delta === 0) return trimmed;
    return rippleTrack(trimmed, after.trackId, oldEnd, delta, [p.clipId]);
  },
});

function applyTrim(
  project: Project,
  p: { clipId: string; edge: 'start' | 'end'; toTick: number },
): Project {
  {
    const clip = project.clips[p.clipId];
    if (!clip) return project;
    const minTicks = 1;

    if (clip.kind === 'midi') {
      if (p.edge === 'end') {
        const duration = Math.max(minTicks, p.toTick - clip.startTicks);
        return withClips(project, [p.clipId], (c) =>
          c.kind === 'midi' ? ({ ...c, durationTicks: duration } as MidiClip) : c,
        );
      }
      const end = clip.startTicks + clip.durationTicks;
      const start = Math.max(0, Math.min(p.toTick, end - minTicks));
      const shift = start - clip.startTicks;
      return withClips(project, [p.clipId], (c) =>
        c.kind === 'midi'
          ? ({
              ...c,
              startTicks: start,
              durationTicks: c.durationTicks - shift,
              notes: c.notes.map((n) => ({ ...n, startTicks: n.startTicks - shift })).filter((n) => n.startTicks + n.durationTicks > 0),
            } as MidiClip)
          : c,
      );
    }

    // Both edges are worked out in heard order — how much of what the clip
    // plays is dropped, how much kept — and `recutSource` turns that into
    // source coordinates from whichever end the clip is heard from.
    const audio = clip;
    if (p.edge === 'end') {
      const end = Math.max(audio.startTicks + minTicks, p.toTick);
      const newSourceSec = sourceSecondsForSpan(project, audio, audio.startTicks, end);
      const durationSec = Math.max(0.001, Math.min(newSourceSec, audio.durationSec + headroomAfter(project, audio)));
      return withClips(project, [p.clipId], (c) =>
        c.kind === 'audio'
          ? {
              ...c,
              ...recutSource(project, c, 0, durationSec),
              ...sliceWarp(c, 0, durationSec),
              fadeOut: clampFade(c.fadeOut, durationSec),
            }
          : c,
      );
    }

    // The start can be dragged earlier only as far as there is source to play:
    // past that the edge stops, rather than the clip sliding along with it.
    const oldEnd = clipEndTicks(project, audio);
    const earliest = tickAfterSourceSeconds(project, audio, audio.startTicks, -headroomBefore(project, audio));
    const start = Math.max(0, Math.ceil(earliest), Math.min(p.toTick, oldEnd - minTicks));
    const leadSec = sourceSecondsForSpan(project, audio, audio.startTicks, start);
    const durationSec = Math.max(0.001, audio.durationSec - leadSec);
    return withClips(project, [p.clipId], (c) =>
      c.kind === 'audio'
        ? {
            ...c,
            startTicks: start,
            ...recutSource(project, c, leadSec, durationSec),
            ...sliceWarp(c, leadSec, durationSec),
            fadeIn: clampFade(c.fadeIn, durationSec),
          }
        : c,
    );
  }
}

function clampFade(fade: ClipFade, clipDuration: number): ClipFade {
  return fade.durationSec > clipDuration ? { ...fade, durationSec: clipDuration } : fade;
}

export const splitClips = defineCommand<{ clipIds: string[]; tick: number }>({
  type: 'clip.split',
  label: () => 'Split clip',
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    const tick = intParam(raw.tick, 0, Number.MAX_SAFE_INTEGER);
    return clipIds && clipIds.length > 0 && tick !== null ? { clipIds, tick } : null;
  },
  apply: (project, p) => {
    let next = project;
    const created: Clip[] = [];
    const shortened: string[] = [];

    for (const clipId of p.clipIds) {
      const clip = next.clips[clipId];
      if (!clip) continue;
      const end = clipEndTicks(next, clip);
      // A split exactly on an edge is a no-op, not an empty clip.
      if (p.tick <= clip.startTicks || p.tick >= end) continue;

      if (clip.kind === 'midi') {
        const relative = p.tick - clip.startTicks;
        const right: MidiClip = {
          ...clip,
          id: newClipId(),
          startTicks: p.tick,
          durationTicks: clip.durationTicks - relative,
          notes: clip.notes
            .filter((n) => n.startTicks + n.durationTicks > relative)
            .map((n) => ({
              ...n,
              id: newNoteId(),
              startTicks: Math.max(0, n.startTicks - relative),
              durationTicks: n.startTicks < relative ? n.startTicks + n.durationTicks - relative : n.durationTicks,
            })),
        };
        created.push(right);
        next = withClips(next, [clipId], (c) =>
          c.kind === 'midi'
            ? {
                ...c,
                durationTicks: relative,
                notes: c.notes
                  .filter((n) => n.startTicks < relative)
                  .map((n) => ({ ...n, durationTicks: Math.min(n.durationTicks, relative - n.startTicks) })),
              }
            : c,
        );
        shortened.push(clipId);
        continue;
      }

      // In heard order: the left piece keeps the first `leftSourceSec` of what
      // the clip plays, the right piece the rest. Which end of the source that
      // is depends on the direction the clip is read, and is not decided here.
      const leftSourceSec = sourceSecondsForSpan(next, clip, clip.startTicks, p.tick);
      const right: AudioClip = {
        ...clip,
        id: newClipId(),
        startTicks: p.tick,
        ...recutSource(next, clip, leftSourceSec, clip.durationSec - leftSourceSec),
        ...sliceWarp(clip, leftSourceSec, clip.durationSec - leftSourceSec),
        fadeIn: { durationSec: 0, curve: clip.fadeIn.curve, curvature: clip.fadeIn.curvature },
      };
      created.push(right);
      next = withClips(next, [clipId], (c) =>
        c.kind === 'audio'
          ? {
              ...c,
              ...recutSource(next, c, 0, leftSourceSec),
              ...sliceWarp(c, 0, leftSourceSec),
              fadeOut: { durationSec: 0, curve: c.fadeOut.curve, curvature: c.fadeOut.curvature },
            }
          : c,
      );
      shortened.push(clipId);
    }

    if (created.length === 0) return project;
    next = insertClips(next, created);
    for (const trackId of new Set(shortened.map((id) => next.clips[id]?.trackId).filter(Boolean) as string[])) {
      next = resortTrackClips(next, trackId);
    }
    return next;
  },
});

export const duplicateClips = defineCommand<{ clipIds: string[]; offsetTicks: number | null }>({
  type: 'clip.duplicate',
  label: (p) => (p.clipIds.length === 1 ? 'Duplicate clip' : `Duplicate ${p.clipIds.length} clips`),
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    if (!clipIds || clipIds.length === 0) return null;
    const offsetTicks = raw.offsetTicks === undefined ? null : intParam(raw.offsetTicks, 0, Number.MAX_SAFE_INTEGER);
    return { clipIds, offsetTicks };
  },
  apply: (project, p) => {
    const clips = p.clipIds.map((id) => project.clips[id]).filter((c): c is Clip => Boolean(c));
    if (clips.length === 0) return project;

    // Default placement: immediately after the selection, so repeated
    // duplication lays copies end to end.
    const offset =
      p.offsetTicks ??
      Math.max(...clips.map((c) => clipEndTicks(project, c))) - Math.min(...clips.map((c) => c.startTicks));

    const copies = clips.map((clip) => ({ ...clip, id: newClipId(), startTicks: clip.startTicks + offset }));
    return insertClips(project, copies);
  },
});

export const setClipGain = defineCommand<{ clipIds: string[]; gain: number }>({
  type: 'clip.setGain',
  label: () => 'Set clip gain',
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    const gain = numberParam(raw.gain, 0, 8);
    return clipIds && clipIds.length > 0 && gain !== null ? { clipIds, gain } : null;
  },
  coalesceKey: (p) => `clipGain:${p.clipIds.join(',')}`,
  apply: (project, p) => withClips(project, p.clipIds, (c) => (c.kind === 'audio' ? { ...c, gain: p.gain } : c)),
});

/**
 * Plays a clip backwards.
 *
 * Reversal is a property of the clip, not a new asset: the file is untouched,
 * the engine reads a reversed copy of it, and unreversing is setting the flag
 * back. That is what keeps it undoable, savable and free — and what lets two
 * clips reading the same file backwards share one reversed render.
 *
 * **Warp markers turn round with the audio.** A marker pins a moment of the
 * source to a beat, and reversing the clip reverses both halves of that: the
 * moment is now its distance from the far end, the beat its distance from the
 * last one. That is the one reading under which a reversed warped clip is the
 * forwards one played backwards — the late hit that was pulled onto beat 3
 * arrives, reversed, at beat 5 — and it is what makes the flag still a flag:
 * turning it off mirrors the markers back to exactly what they were.
 */
export const setClipReversed = defineCommand<{ clipIds: string[]; reversed: boolean }>({
  type: 'clip.setReversed',
  label: (p) => (p.reversed ? 'Reverse clip' : 'Play clip forwards'),
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    const reversed = boolParam(raw.reversed);
    return clipIds && clipIds.length > 0 && reversed !== null ? { clipIds, reversed } : null;
  },
  apply: (project, p) =>
    withClips(project, p.clipIds, (clip) =>
      clip.kind === 'audio' && clip.reversed !== p.reversed
        ? { ...clip, reversed: p.reversed, warpMarkers: mirrorWarpMarkers(clip) }
        : clip,
    ),
});

/**
 * Transposes a clip, without changing how long it is.
 *
 * Separate from `setClipRate` on purpose. Speed and pitch are two controls
 * because they are two questions — how long should this be, and what note should
 * it sound — and a musician who has fitted a loop to the bar should be able to
 * transpose it without the fit coming undone. The engine composes them: the
 * length the rate asks for, at the pitch this asks for.
 *
 * It applies to a varispeed clip too, where the speed has already moved the
 * pitch. There the shift stacks on top rather than replacing it, so setting -12
 * on a clip played at double speed puts it back at the note it was recorded at,
 * still taking half the time. Any other reading would make one control silently
 * undo the other.
 */
export const setClipPitch = defineCommand<{ clipIds: string[]; semitones: number }>({
  type: 'clip.setPitch',
  label: () => 'Transpose clip',
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    const semitones = numberParam(raw.semitones, MIN_PITCH_SEMITONES, MAX_PITCH_SEMITONES);
    return clipIds && clipIds.length > 0 && semitones !== null ? { clipIds, semitones } : null;
  },
  coalesceKey: (p) => `clipPitch:${p.clipIds.join(',')}`,
  apply: (project, p) =>
    withClips(project, p.clipIds, (c) => (c.kind === 'audio' ? { ...c, pitchSemitones: p.semitones } : c)),
});

/**
 * Says where a clip's formants should sit.
 *
 * Separate from `setClipPitch` because it answers a different question: not how
 * far to move the note, but what should happen to the body making it. A voice
 * usually wants them held; a synth sweep may well want them carried along.
 * Nothing decides that automatically, because nothing can hear what the clip is.
 *
 * `null` is "wherever the pitch takes them" and is not a position, so it is not
 * a number. Everything else is a position, measured from where the recording
 * put them — which is why a formant move works on a clip that is not transposed
 * at all.
 */
export const setClipFormants = defineCommand<{ clipIds: string[]; semitones: number | null }>({
  type: 'clip.setFormants',
  label: (p) => (p.semitones === null ? 'Let formants follow the pitch' : 'Move clip formants'),
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    if (!clipIds || clipIds.length === 0) return null;
    if (raw.semitones === null) return { clipIds, semitones: null };
    const semitones = numberParam(raw.semitones, MIN_PITCH_SEMITONES, MAX_PITCH_SEMITONES);
    return semitones === null ? null : { clipIds, semitones };
  },
  coalesceKey: (p) => `clipFormants:${p.clipIds.join(',')}`,
  apply: (project, p) =>
    withClips(project, p.clipIds, (c) =>
      c.kind === 'audio' && c.formantSemitones !== p.semitones ? { ...c, formantSemitones: p.semitones } : c,
    ),
});

export const setClipRate = defineCommand<{ clipIds: string[]; rate: number; preservePitch: boolean | null }>({
  type: 'clip.setRate',
  label: () => 'Set clip speed',
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    const rate = numberParam(raw.rate, MIN_PLAYBACK_RATE, MAX_PLAYBACK_RATE);
    if (!clipIds || clipIds.length === 0 || rate === null) return null;
    const preservePitch = omitted(raw.preservePitch) ? null : boolParam(raw.preservePitch);
    if (raw.preservePitch !== undefined && raw.preservePitch !== null && preservePitch === null) return null;
    return { clipIds, rate, preservePitch };
  },
  coalesceKey: (p) => `clipRate:${p.clipIds.join(',')}`,
  apply: (project, p) => {
    const map = tempoMapOf(project);
    return withClips(project, p.clipIds, (c) => {
      if (c.kind !== 'audio') return c;
      const preservePitch = p.preservePitch ?? c.preservePitch;
      // A clip that follows the tempo has no speed of its own to set. Asking it
      // to play at a given speed is a statement about what tempo it was recorded
      // at, so that is what changes — and it keeps following.
      if (c.warpSourceBpm !== null) {
        return { ...c, preservePitch, warpSourceBpm: warpBpmForRate(map, c, p.rate) };
      }
      return { ...c, playbackRate: p.rate, preservePitch };
    });
  },
});

/** The source tempo a warped clip needs in order to play at `rate`. */
function warpBpmForRate(map: NormalisedTempoMap, clip: AudioClip, rate: number): number {
  return Math.min(900, Math.max(5, tempoAt(map, clip.startTicks) / Math.max(0.01, rate)));
}

/**
 * Sets the rate so a clip lasts exactly `targetSec` of timeline.
 *
 * This is what stretching a clip's edge, and fitting a loop to the project
 * tempo, both come down to: the source length is fixed, the timeline length is
 * given, and the rate is their quotient.
 */
export const fitClipToLength = defineCommand<{ clipId: string; targetSec: number }>({
  type: 'clip.fitToLength',
  label: () => 'Stretch clip',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const targetSec = numberParam(raw.targetSec, 0.001, 60 * 60);
    return clipId && targetSec !== null ? { clipId, targetSec } : null;
  },
  coalesceKey: (p) => `clipFit:${p.clipId}`,
  apply: (project, p) => {
    const clip = project.clips[p.clipId];
    if (!clip || clip.kind !== 'audio') return project;
    const sourceSec = clip.durationSec;
    if (sourceSec <= 0) return project;
    const rate = Math.min(MAX_PLAYBACK_RATE, Math.max(MIN_PLAYBACK_RATE, sourceSec / p.targetSec));
    if (rate === effectivePlaybackRate(project, clip)) return project;
    const map = tempoMapOf(project);
    return withClips(project, [p.clipId], (c) =>
      c.kind !== 'audio'
        ? c
        : c.warpSourceBpm !== null
          ? { ...c, warpSourceBpm: warpBpmForRate(map, c, rate) }
          : { ...c, playbackRate: rate },
    );
  },
});

/**
 * Makes a clip follow the project tempo, or stops it following.
 *
 * Turning it on with no source tempo derives one from the speed the clip is
 * already playing at, so nothing moves at the moment you switch it on; the clip
 * only starts behaving differently when the project tempo next changes. Turning
 * it off bakes the speed it had arrived at into `playbackRate`, for the same
 * reason. A control that jumps the audio the instant it is touched is one people
 * stop touching.
 */
export const setClipWarp = defineCommand<{ clipIds: string[]; follow: boolean; sourceBpm: number | null }>({
  type: 'clip.setWarp',
  label: (p) => (p.follow ? 'Follow project tempo' : 'Stop following tempo'),
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    const follow = boolParam(raw.follow);
    if (!clipIds || clipIds.length === 0 || follow === null) return null;
    const sourceBpm = omitted(raw.sourceBpm) ? null : numberParam(raw.sourceBpm, 5, 900);
    if (raw.sourceBpm !== undefined && raw.sourceBpm !== null && sourceBpm === null) return null;
    return { clipIds, follow, sourceBpm };
  },
  coalesceKey: (p) => (p.sourceBpm === null ? null : `clipWarp:${p.clipIds.join(',')}`),
  apply: (project, p) => {
    const map = tempoMapOf(project);
    return withClips(project, p.clipIds, (c) => {
      if (c.kind !== 'audio') return c;
      if (!p.follow) {
        if (c.warpSourceBpm === null) return c;
        // Keep the speed it was playing at, now as its own property.
        return { ...c, warpSourceBpm: null, playbackRate: effectivePlaybackRate(project, c) };
      }
      const projectBpm = tempoAt(map, c.startTicks);
      const sourceBpm =
        p.sourceBpm ?? (c.warpSourceBpm ?? projectBpm / effectivePlaybackRate(project, c));
      if (c.warpSourceBpm === sourceBpm) return c;
      return { ...c, warpSourceBpm: Math.min(900, Math.max(5, sourceBpm)) };
    });
  },
});

/**
 * Pins a moment in a clip's audio to a musical position inside it.
 *
 * Only meaningful on a clip that follows the tempo: a fixed clip has no musical
 * grid of its own to pin anything to. Markers too close to a neighbour, or to
 * the clip's own pinned start and end, are dropped when the curve is built
 * rather than allowed to make the map fold back on itself.
 */
export const addWarpMarker = defineCommand<{
  clipId: string;
  markerId: string;
  sourceSec: number;
  beat: number;
}>({
  type: 'clip.addWarpMarker',
  label: () => 'Add warp marker',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const markerId = raw.markerId === undefined ? newWarpMarkerId() : idParam(raw.markerId);
    const sourceSec = numberParam(raw.sourceSec, 0, 60 * 60 * 4);
    const beat = numberParam(raw.beat, 0, 100000);
    if (!clipId || !markerId || sourceSec === null || beat === null) return null;
    return { clipId, markerId, sourceSec, beat };
  },
  apply: (project, p) => {
    const clip = project.clips[p.clipId];
    if (!clip || clip.kind !== 'audio' || clip.warpSourceBpm === null) return project;
    if (clip.warpMarkers.some((m) => m.id === p.markerId)) return project;
    const markers = [...clip.warpMarkers, { id: p.markerId, sourceSec: p.sourceSec, beat: p.beat }].sort(
      (a, b) => a.sourceSec - b.sourceSec,
    );
    return withClips(project, [p.clipId], (c) => (c.kind === 'audio' ? { ...c, warpMarkers: markers } : c));
  },
});

/**
 * Moves where a marker's moment of audio lands, in beats.
 *
 * Only the musical side moves. Dragging a marker is a statement about *when*
 * that sound should happen, not about which sound it is; letting the source side
 * move too would mean the marker no longer marked anything in particular.
 */
export const moveWarpMarker = defineCommand<{ clipId: string; markerId: string; beat: number }>({
  type: 'clip.moveWarpMarker',
  label: () => 'Move warp marker',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const markerId = idParam(raw.markerId);
    const beat = numberParam(raw.beat, 0, 100000);
    return clipId && markerId && beat !== null ? { clipId, markerId, beat } : null;
  },
  coalesceKey: (p) => `warpMarker:${p.clipId}:${p.markerId}`,
  apply: (project, p) => {
    const clip = project.clips[p.clipId];
    if (!clip || clip.kind !== 'audio') return project;
    const existing = clip.warpMarkers.find((m) => m.id === p.markerId);
    if (!existing || existing.beat === p.beat) return project;
    const markers = clip.warpMarkers.map((m) => (m.id === p.markerId ? { ...m, beat: p.beat } : m));
    return withClips(project, [p.clipId], (c) => (c.kind === 'audio' ? { ...c, warpMarkers: markers } : c));
  },
});

export const removeWarpMarker = defineCommand<{ clipId: string; markerId: string }>({
  type: 'clip.removeWarpMarker',
  label: () => 'Remove warp marker',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const markerId = idParam(raw.markerId);
    return clipId && markerId ? { clipId, markerId } : null;
  },
  apply: (project, p) => {
    const clip = project.clips[p.clipId];
    if (!clip || clip.kind !== 'audio') return project;
    const markers = clip.warpMarkers.filter((m) => m.id !== p.markerId);
    if (markers.length === clip.warpMarkers.length) return project;
    return withClips(project, [p.clipId], (c) => (c.kind === 'audio' ? { ...c, warpMarkers: markers } : c));
  },
});

export const clearWarpMarkers = defineCommand<{ clipIds: string[] }>({
  type: 'clip.clearWarpMarkers',
  label: () => 'Clear warp markers',
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    return clipIds && clipIds.length > 0 ? { clipIds } : null;
  },
  apply: (project, p) => {
    const anything = p.clipIds.some((id) => {
      const clip = project.clips[id];
      return clip?.kind === 'audio' && clip.warpMarkers.length > 0;
    });
    if (!anything) return project;
    return withClips(project, p.clipIds, (c) => (c.kind === 'audio' ? { ...c, warpMarkers: [] } : c));
  },
});

export const setClipFade = defineCommand<{
  clipId: string;
  edge: 'in' | 'out';
  durationSec: number | null;
  curve: FadeCurve | null;
  curvature: number | null;
}>({
  type: 'clip.setFade',
  label: (p) => (p.edge === 'in' ? 'Set fade in' : 'Set fade out'),
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const edge = enumParam(raw.edge, ['in', 'out'] as const);
    if (!clipId || !edge) return null;

    // Length, law and shape are all optional so a drag can change one of them
    // without having to restate the other two.
    const durationSec = omitted(raw.durationSec) ? null : numberParam(raw.durationSec, 0, 600);
    if (!omitted(raw.durationSec) && durationSec === null) return null;
    const curvature = omitted(raw.curvature) ? null : numberParam(raw.curvature, -1, 1);
    if (!omitted(raw.curvature) && curvature === null) return null;
    if (durationSec === null && curvature === null && omitted(raw.curve)) return null;

    return { clipId, edge, durationSec, curve: enumParam(raw.curve, FADE_CURVES), curvature };
  },
  coalesceKey: (p) => `fade:${p.clipId}:${p.edge}:${p.curvature !== null ? 'shape' : 'length'}`,
  apply: (project, p) =>
    withClips(project, [p.clipId], (c) => {
      if (c.kind !== 'audio') return c;
      const existing = p.edge === 'in' ? c.fadeIn : c.fadeOut;
      const fade: ClipFade = {
        durationSec: p.durationSec === null ? existing.durationSec : Math.min(p.durationSec, c.durationSec),
        curve: p.curve ?? existing.curve,
        curvature: p.curvature ?? existing.curvature,
      };
      return p.edge === 'in' ? { ...c, fadeIn: fade } : { ...c, fadeOut: fade };
    }),
});

export const setClipMuted = defineCommand<{ clipIds: string[]; muted: boolean }>({
  type: 'clip.setMuted',
  label: (p) => (p.muted ? 'Mute clip' : 'Unmute clip'),
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    const muted = boolParam(raw.muted);
    return clipIds && clipIds.length > 0 && muted !== null ? { clipIds, muted } : null;
  },
  apply: (project, p) => withClips(project, p.clipIds, (c) => ({ ...c, muted: p.muted })),
});

export const renameClip = defineCommand<{ clipId: string; name: string }>({
  type: 'clip.rename',
  label: () => 'Rename clip',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const name = stringParam(raw.name, 128);
    return clipId && name ? { clipId, name } : null;
  },
  coalesceKey: (p) => `clipName:${p.clipId}`,
  apply: (project, p) => withClips(project, [p.clipId], (c) => ({ ...c, name: p.name })),
});

/** Pastes previously copied clips at a tick, on their original or a target track. */
export const pasteClips = defineCommand<{ clips: Clip[]; atTick: number; targetTrackId: string | null }>({
  type: 'clip.paste',
  label: (p) => (p.clips.length === 1 ? 'Paste clip' : `Paste ${p.clips.length} clips`),
  parse: (raw) => {
    if (!Array.isArray(raw.clips) || raw.clips.length === 0) return null;
    const atTick = intParam(raw.atTick, 0, Number.MAX_SAFE_INTEGER);
    if (atTick === null) return null;
    // Clip payloads are re-validated through the project parser's clip reader by
    // routing them via a throwaway project on apply; here we only check shape.
    const clips = raw.clips.filter((c): c is Clip => typeof c === 'object' && c !== null && 'kind' in c);
    if (clips.length !== raw.clips.length) return null;
    return { clips, atTick, targetTrackId: raw.targetTrackId === undefined ? null : idParam(raw.targetTrackId) };
  },
  apply: (project, p) => {
    const earliest = Math.min(...p.clips.map((c) => c.startTicks));
    const copies: Clip[] = [];
    for (const clip of p.clips) {
      const trackId = p.targetTrackId ?? clip.trackId;
      if (!project.tracks[trackId]) continue;
      if (clip.kind === 'audio' && !project.assets[clip.assetId]) continue;
      copies.push({
        ...clip,
        id: newClipId(),
        trackId,
        startTicks: Math.max(0, p.atTick + (clip.startTicks - earliest)),
      });
    }
    return copies.length > 0 ? insertClips(project, copies) : project;
  },
});

/** Length of a clip in ticks — re-exported so UI code has one import site. */
export { clipLengthTicks };
